import { useState, useEffect } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { Database } from '@/../database.types'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Badge } from '@/components/ui/badge'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import TaskAssignmentEditor from '@/components/custom-ui/task-assignment-editor'
import TaskAssignmentBlocks from '@/components/custom-ui/task-assignment-blocks'
import { usePermissions } from '@/lib/hooks'
import { RoleNotification } from '@/components/role-notification'

type Task = Database['public']['Tables']['tasks']['Row']

interface TaskDetailProps {
  task: Task | null
  open: boolean
  onOpenChange: (open: boolean) => void
  onSuccess?: () => void
}

export default function TaskDetail({ task, open, onOpenChange, onSuccess }: TaskDetailProps) {
  const [editedTask, setEditedTask] = useState<Task | null>(null)
  const queryClient = useQueryClient()
  const permissions = usePermissions()

  useEffect(() => {
    if (task) {
      setEditedTask(task)
    }
  }, [task])

  // Update task mutation
  const updateTaskMutation = useMutation({
    mutationFn: async (updatedTask: Task) => {
      const { error } = await supabase
        .from('tasks')
        .update({
          title: updatedTask.title,
          description: updatedTask.description,
          category: updatedTask.category,
          tshirt_size_effort: updatedTask.tshirt_size_effort,
          tshirt_size_capital: updatedTask.tshirt_size_capital,
          frequency: updatedTask.frequency,
          founder_leverage: updatedTask.founder_leverage,
          status: updatedTask.status,
          estimated_effort_hours: updatedTask.estimated_effort_hours,
          start_date: updatedTask.start_date,
          due_date: updatedTask.due_date,
          completion_date: updatedTask.completion_date,
          priority: updatedTask.priority,
          tags: updatedTask.tags,
          dependencies: updatedTask.dependencies,
          milestone_id: updatedTask.milestone_id,
          updated_at: new Date().toISOString()
        })
        .eq('id', updatedTask.id)

      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tasks'] })
      onSuccess?.()
      onOpenChange(false)
    }
  })

  const handleSave = () => {
    if (editedTask) {
      updateTaskMutation.mutate(editedTask)
    }
  }

  const getSizeColor = (size: string | null) => {
    if (!size) return 'bg-gray-100 text-gray-800'
    switch (size) {
      case 'S': return 'bg-green-100 text-green-800'
      case 'M': return 'bg-yellow-100 text-yellow-800'
      case 'L': return 'bg-orange-100 text-orange-800'
      case 'XL': return 'bg-red-100 text-red-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  if (!editedTask) return null

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>Task Details</DialogTitle>
        </DialogHeader>

        <div className="grid gap-6">
          {!permissions.canEditTasks && (
            <RoleNotification 
              feature="editTasks" 
              customMessage="You can view task details but cannot make changes. Only project members can edit tasks."
            />
          )}
          <div className="grid gap-4">
            <div>
              <Label htmlFor="title">Title</Label>
              <Input
                id="title"
                value={editedTask.title}
                onChange={(e) => setEditedTask({ ...editedTask, title: e.target.value })}
                disabled={!permissions.canEditTasks}
              />
            </div>

            <div>
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={editedTask.description || ''}
                onChange={(e) => setEditedTask({ ...editedTask, description: e.target.value })}
                rows={3}
                placeholder="Add a description..."
                disabled={!permissions.canEditTasks}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="category">Category</Label>
                <Select
                  value={editedTask.category || ''}
                  onValueChange={(value) => setEditedTask({ ...editedTask, category: value })}
                  disabled={!permissions.canEditTasks}
                >
                  <SelectTrigger id="category">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Product">Product</SelectItem>
                    <SelectItem value="Engineering">Engineering</SelectItem>
                    <SelectItem value="Marketing">Marketing</SelectItem>
                    <SelectItem value="Sales">Sales</SelectItem>
                    <SelectItem value="Operations">Operations</SelectItem>
                    <SelectItem value="Finance">Finance</SelectItem>
                    <SelectItem value="Legal">Legal</SelectItem>
                    <SelectItem value="HR">HR</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="status">Status</Label>
                <Select
                  value={editedTask.status || 'pending'}
                  onValueChange={(value) => setEditedTask({ ...editedTask, status: value })}
                  disabled={!permissions.canEditTasks}
                >
                  <SelectTrigger id="status">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="in_progress">In Progress</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                    <SelectItem value="cancelled">Cancelled</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="effort">Effort Size</Label>
                <Select
                  value={editedTask.tshirt_size_effort || ''}
                  onValueChange={(value) => setEditedTask({ ...editedTask, tshirt_size_effort: value })}
                  disabled={!permissions.canEditTasks}
                >
                  <SelectTrigger id="effort">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="S">Small</SelectItem>
                    <SelectItem value="M">Medium</SelectItem>
                    <SelectItem value="L">Large</SelectItem>
                    <SelectItem value="XL">Extra Large</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="capital">Capital Size</Label>
                <Select
                  value={editedTask.tshirt_size_capital || ''}
                  onValueChange={(value) => setEditedTask({ ...editedTask, tshirt_size_capital: value })}
                  disabled={!permissions.canEditTasks}
                >
                  <SelectTrigger id="capital">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="S">Small</SelectItem>
                    <SelectItem value="M">Medium</SelectItem>
                    <SelectItem value="L">Large</SelectItem>
                    <SelectItem value="XL">Extra Large</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="frequency">Frequency</Label>
                <Select
                  value={editedTask.frequency || ''}
                  onValueChange={(value) => setEditedTask({ ...editedTask, frequency: value })}
                  disabled={!permissions.canEditTasks}
                >
                  <SelectTrigger id="frequency">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="One-time">One-time</SelectItem>
                    <SelectItem value="Daily">Daily</SelectItem>
                    <SelectItem value="Weekly">Weekly</SelectItem>
                    <SelectItem value="Monthly">Monthly</SelectItem>
                    <SelectItem value="Quarterly">Quarterly</SelectItem>
                    <SelectItem value="Yearly">Yearly</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="leverage">Founder Leverage</Label>
                <Select
                  value={editedTask.founder_leverage || ''}
                  onValueChange={(value) => setEditedTask({ ...editedTask, founder_leverage: value })}
                  disabled={!permissions.canEditTasks}
                >
                  <SelectTrigger id="leverage">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Low">Low</SelectItem>
                    <SelectItem value="Medium">Medium</SelectItem>
                    <SelectItem value="High">High</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          <Tabs defaultValue="current" className="w-full">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="current">Current Assignments</TabsTrigger>
              <TabsTrigger value="timeline">Assignment Timeline</TabsTrigger>
            </TabsList>
            <TabsContent value="current" className="mt-4">
              <TaskAssignmentEditor 
                taskId={editedTask.id} 
                projectId={editedTask.project_id}
              />
            </TabsContent>
            <TabsContent value="timeline" className="mt-4">
              <TaskAssignmentBlocks 
                taskId={editedTask.id} 
                projectId={editedTask.project_id}
              />
            </TabsContent>
          </Tabs>

          <div className="flex justify-between items-center">
            <div className="flex gap-2">
              <Badge className={getSizeColor(editedTask.tshirt_size_effort)}>
                Effort: {editedTask.tshirt_size_effort || 'Not set'}
              </Badge>
              <Badge className={getSizeColor(editedTask.tshirt_size_capital)}>
                Capital: {editedTask.tshirt_size_capital || 'Not set'}
              </Badge>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              {permissions.canEditTasks && (
                <Button onClick={handleSave} disabled={updateTaskMutation.isPending}>
                  Save Changes
                </Button>
              )}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}